<template>
  <div class="quiet-schedule pb-16" :lang="domLanguage">
    <section class="schedule-heading" aria-labelledby="schedule-title">
      <img class="schedule-heading__crown" src="@/assets/deco/line_castle.png" alt="" aria-hidden="true" />
      <h2 id="schedule-title" class="schedule-heading__title font-danjunghae" :lang="domLanguage">
        {{ $t("page.mypage.reserv.upcoming-title") }}
      </h2>
    </section>

    <v-sheet class="quiet-schedule__body mx-auto">
      <!-- 불러오는 중 / 실패는 '예약 없음'과 구분한다 -->
      <section v-if="loadState !== 'ready' && !list.length" class="upcoming-empty" role="status" aria-live="polite">
        <template v-if="loadState === 'loading'">
          <v-progress-circular indeterminate size="28" width="3" color="#d74369" />
          <p class="list-state__text">{{ $t("page.mypage.reserv.loading") }}</p>
        </template>
        <template v-else>
          <p class="list-state__text">{{ $t("page.mypage.reserv.load-failed") }}</p>
          <v-btn depressed class="upcoming-empty__button" @click="load">
            {{ $t("page.mypage.reserv.reload-button") }}
          </v-btn>
        </template>
      </section>

      <section v-else-if="upcomingReservation" class="upcoming-reservation" aria-labelledby="upcoming-service-title">
        <div
          class="upcoming-reservation__photo"
          :style="{
            backgroundImage: `url(${getReservationThumbnail(upcomingReservation)})`,
            backgroundPosition: getReservationImagePosition(0),
          }"
          role="img"
          :aria-label="`${getServiceTitle(upcomingReservation)} ${getConceptTitle(upcomingReservation)}`"
        ></div>

        <div class="upcoming-reservation__content">
          <button
            type="button"
            class="upcoming-reservation__summary"
            @click="openReservationDetail(upcomingReservation)"
          >
            <span class="upcoming-reservation__copy">
              <span id="upcoming-service-title" class="upcoming-reservation__title" :lang="domLanguage">
                <span class="upcoming-reservation__service">{{ getServiceTitle(upcomingReservation) }}</span>
                <span class="upcoming-reservation__separator" aria-hidden="true"> · </span>
                <span class="upcoming-reservation__concept">{{ getConceptTitle(upcomingReservation) }}</span>
              </span>
              <span class="upcoming-reservation__date">
                {{ formatReservationDate(upcomingReservation) }}
              </span>
            </span>
            <span class="upcoming-reservation__status">{{ $t("page.mypage.reserv.shooting-scheduled") }}</span>
            <v-icon class="upcoming-reservation__chevron" size="20" aria-hidden="true">mdi-chevron-right</v-icon>
          </button>
          <div class="upcoming-reservation__actions">
            <v-btn text class="reservation-card-action reservation-card-action--notice" @click="openCautionDialog(upcomingReservation)">
              <v-icon left size="17">mdi-information-outline</v-icon>
              <span class="reservation-card-action__label">{{ $t("page.mypage.reserv.notice-button") }}</span>
            </v-btn>
            <v-btn
              depressed
              class="reservation-card-action reservation-card-action--original"
              :disabled="isCanceled(upcomingReservation) || isPhotoActionDisabled(upcomingReservation)"
              @click="onClickCheckOriginal(upcomingReservation)"
            >
              <v-icon left size="17">mdi-image-multiple-outline</v-icon>
              <span class="reservation-card-action__label">{{ getPhotoActionLabel(upcomingReservation) }}</span>
            </v-btn>
          </div>
        </div>
      </section>

      <section v-else class="upcoming-empty" aria-labelledby="upcoming-empty-title">
        <h3 id="upcoming-empty-title" class="upcoming-empty__title font-danjunghae">
          {{ $t("page.mypage.reserv.upcoming-empty") }}
        </h3>
        <v-btn depressed class="upcoming-empty__button" @click="newReservation">
          {{ $t("page.mypage.reserv.new-reservation-button") }}
        </v-btn>
      </section>

      <section v-if="otherReservations.length" class="other-reservations" aria-labelledby="other-reservations-title">
        <div class="other-reservations__heading">
          <h3 id="other-reservations-title" class="font-danjunghae">{{ $t("page.mypage.reserv.other-title") }}</h3>
          <span>{{ otherReservations.length }}</span>
        </div>

        <div class="other-reservations__list">
          <article
            v-for="(item, index) in otherReservations"
            :key="item.id"
            class="other-reservation-card"
            :class="{ 'other-reservation-card--cancelled': isCanceled(item) }"
          >
            <span
              class="other-reservation-card__thumbnail"
              :style="{
                backgroundImage: `url(${getReservationThumbnail(item)})`,
                backgroundPosition: getReservationImagePosition(index + 1),
              }"
              aria-hidden="true"
            ></span>
            <button type="button" class="other-reservation-card__summary" @click="openReservationDetail(item)">
              <span class="other-reservation-card__copy">
                <span class="other-reservation-card__title">{{ getServiceTitle(item) }} · {{ getConceptTitle(item) }}</span>
                <span class="other-reservation-card__date">{{ formatReservationDate(item) }}</span>
              </span>
              <span class="other-reservation-card__status">{{ getReservationStatusText(item) }}</span>
              <v-icon class="other-reservation-card__chevron" size="20" aria-hidden="true">mdi-chevron-right</v-icon>
            </button>
            <div class="other-reservation-card__actions">
              <v-btn text class="reservation-card-action reservation-card-action--notice" @click="openCautionDialog(item)">
                <v-icon left size="17">mdi-information-outline</v-icon>
                <span class="reservation-card-action__label">{{ $t("page.mypage.reserv.notice-button") }}</span>
              </v-btn>
              <v-btn
                depressed
                class="reservation-card-action reservation-card-action--original"
                :disabled="isCanceled(item) || isPhotoActionDisabled(item)"
                @click="onClickCheckOriginal(item)"
              >
                <v-icon left size="17">mdi-image-multiple-outline</v-icon>
                <span class="reservation-card-action__label">{{ getPhotoActionLabel(item) }}</span>
              </v-btn>
            </div>
          </article>
        </div>
      </section>

    </v-sheet>

    <v-dialog v-model="dialog.detail" max-width="560" content-class="reservation-detail-dialog">
      <v-sheet
        v-if="detailItem"
        class="reservation-detail"
      >
        <header class="reservation-detail__header">
          <div
            class="reservation-detail__thumbnail"
            :class="{ 'reservation-detail__thumbnail--cancelled': isCanceled(detailItem) }"
            :style="{
              backgroundImage: `url(${getReservationThumbnail(detailItem)})`,
              backgroundPosition: getReservationImagePosition(0),
            }"
            aria-hidden="true"
          ></div>
          <div>
            <span class="reservation-detail__status">{{ getReservationStatusText(detailItem) }}</span>
            <h3 class="reservation-detail__title font-danjunghae">{{ getServiceTitle(detailItem) }}</h3>
            <p>{{ getConceptTitle(detailItem) }}</p>
          </div>
          <v-btn icon class="reservation-detail__close" :aria-label="$t('button.close')" @click="dialog.detail = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <dl class="reservation-detail__facts">
          <div :class="{ 'reservation-detail__time-row': mayChangeTimeByDate(detailItem) && !detailItem.timeChangeUsedUp }">
            <dt>{{ $t("page.mypage.reserv.visit-time") }}</dt>
            <dd class="reservation-detail__time">
              <span>{{ formatReservationDate(detailItem) }}</span>
              <button v-if="canChangeTime(detailItem)" ref="timeChangeButton" type="button" class="reservation-detail__time-change" @click="timeChangeOpen = true">
                {{ $t("page.mypage.reserv.time-change.button") }}
              </button>
            </dd>
          </div>
          <div v-if="getServiceKey(detailItem) === 'makershot'">
            <dt>{{ $t("page.mypage.reserv.shooting-time") }}</dt>
            <dd>{{ getShootingTime(detailItem.times) }}</dd>
          </div>
          <div>
            <dt>{{ $t("page.mypage.reserv.concept-title") }}</dt>
            <dd>{{ getConceptTitle(detailItem) }}</dd>
          </div>
          <div>
            <dt>{{ $t("page.mypage.reserv.people-title") }}</dt>
            <dd>{{ $t("page.mypage.reserv.person-value", { count: getPersonCount(detailItem) }) }}</dd>
          </div>
          <div v-if="getSelectedProductTitle(detailItem)">
            <dt>{{ $t("page.mypage.reserv.product-title") }}</dt>
            <dd>{{ getSelectedProductTitle(detailItem) }}</dd>
          </div>
          <div v-if="isTossPayment(detailItem)">
            <dt>{{ $t("page.mypage.reserv.payment-status") }}</dt>
            <dd>{{ getPaymentStatusText(detailItem) }} · {{ $toComma(detailItem.pay_amount || 0) }}{{ $t("common.krw") }}</dd>
          </div>
          <div v-if="getCancelAmount(detailItem) > 0">
            <dt>{{ $t("page.mypage.reserv.cancel-amount") }}</dt>
            <dd>{{ $toComma(getCancelAmount(detailItem)) }}{{ $t("common.krw") }}</dd>
          </div>
          <div v-if="getCancelDate(detailItem)">
            <dt>{{ $t("page.mypage.reserv.cancel-time") }}</dt>
            <dd>{{ getCancelDate(detailItem) }}</dd>
          </div>
          <div v-if="getSelectedOptionTitles(detailItem).length > 0">
            <dt>{{ $t("page.mypage.reserv.option-title") }}</dt>
            <dd>{{ getSelectedOptionTitles(detailItem).join(" + ") }}</dd>
          </div>
        </dl>

        <!-- 메이커샷은 원본 확인 흐름이 달라(촬영일부터 7일) 옛 안내를 그대로 둔다 -->
        <div v-if="!isCanceled(detailItem) && getServiceKey(detailItem) === 'makershot'" class="reservation-detail__guide">
          <strong>{{ $t("page.mypage.reserv.visit-after-guide") }}</strong>
          <span>{{ $t("page.mypage.reserv.original-delete-guide") }}</span>
        </div>
        <PhotoDeliveryGuide
          v-else-if="!isCanceled(detailItem) && hasDirectPhotoDelivery(detailItem)"
          class="reservation-detail__photo-guide"
          :lead="$t('page.mypage.reserv.photo-guide-lead')"
          :original="$t('page.mypage.reserv.photo-guide-original')"
          :completed="$t('page.mypage.reserv.photo-guide-completed')"
        />

        <div v-if="getChangeClosedNote(detailItem)" ref="closedNote" class="reservation-detail__closed" tabindex="-1">
          <!-- 문장마다 줄을 나눠, 문장 끝 한두 낱말만 다음 줄로 넘어가지 않게 한다(글자는 그대로) -->
          <p class="reservation-detail__cancel-note">
            <span v-for="line in closedNoteLines(detailItem)" :key="line">{{ line }}</span>
          </p>
          <a v-if="closedNoteAsksStore(detailItem)" class="reservation-detail__dm" :href="instagramDmUrl" target="_blank" rel="noopener noreferrer">
            <v-icon size="18" aria-hidden="true">mdi-instagram</v-icon>
            {{ $t("page.mypage.reserv.time-change.dm-button") }}
          </a>
        </div>

        <div class="reservation-detail__actions">
          <v-btn v-if="isTossPayment(detailItem)" depressed class="detail-action detail-action--primary" @click="openReceipt(detailItem)">
            <v-icon left size="17">mdi-receipt-text-outline</v-icon>
            {{ isCanceled(detailItem) ? $t("page.mypage.reserv.cancel-detail-button") : $t("page.mypage.reserv.receipt-button") }}
          </v-btn>
          <v-btn
            v-if="!detailItem.is_fixture && !isCanceled(detailItem) && !isCantReserveCancel(detailItem.created, detailItem.year, detailItem.month, detailItem.day, detailItem.times.split(',')[0])"
            outlined
            class="detail-action detail-action--cancel"
            @click="openDetailCancel(detailItem)"
          >
            {{ $t("button.cancel-reserve") }}
          </v-btn>
        </div>
        <!-- 시간 변경 창: 예약 상세 안에 두어, 상세 창의 초점 잡기와 부딪히지 않게 한다 -->
        <ReservationTimeChange
          v-if="timeChangeOpen"
          :reservation="detailItem"
          :shoot-offset="getServiceKey(detailItem) === 'makershot' ? shootingOffset : null"
          @changed="onTimeChanged"
          @limit="onTimeChangeLimit"
          @close="closeTimeChange"
        />
      </v-sheet>
    </v-dialog>

    <!-- 예약취소 -->
    <v-dialog v-model="dialog.cancel" max-width="540" content-class="rounded-xl" :key="cancelComponentKey">
      <v-sheet class="rounded-10 px-2 px-md-4 py-4 py-md-10 d-flex flex-column align-center" color="#f7f2f7">
        <img src="@/assets/warning.png" class="icon" />

        <p class="text-h5 grey--text text--darken-2 font-weight-bold mt-2">{{ $t("button.cancel-reserve") }}</p>

        <p class="reservation-cancel__message text-center px-1 text-body-1" :lang="domLanguage" v-html="getRefundDialogMessage(selectedItem)"></p>

        <v-container fluid class="d-flex justify-center mt-6">
          <v-btn color="white" class="dialog_btn rounded-2 mr-2 grey--text" width="35%" style="font-size: 20px" depressed @click="dialog.cancel = false" large>{{ $t("button.no") }}</v-btn>

          <v-btn
            color="#d9bddd"
            class="dialog_btn rounded-2 ml-2"
            style="font-size: 20px"
            width="35%"
            depressed
            @click="reservationCancelSubmit(selectedItem)"
            large
            :loading="reservation_canceling"
            >{{ $t("button.yes") }}</v-btn
          >
        </v-container>
      </v-sheet>
    </v-dialog>
    <CautionMesageDialog
      :readonly="true"
      @agree="show_refund_policy = false"
      @cancel="show_refund_policy = false"
      @input="(e) => (show_refund_policy = e)"
      :visible="show_refund_policy"
      :product-type="refundPolicyProductType"
      :key="'refund_policy_dialog_' + refund_policy_dialog_componentKey"
    />
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex"
import MagicCodes from "./MagicCodes"
import payletterService from "@/api/payletter-service"
import CautionMesageDialog from "./CautionMessageDialogEnhanced"
import ReservationTimeChange from "./ReservationTimeChange"
import { INSTAGRAM_DM_URL, SHOOTING_TIME } from "@/constant"
import reservationImage from "@/assets/home.png"
import PhotoDeliveryGuide from "@/views/components/PhotoDeliveryGuide"
import {
  findUpcomingReservation,
  isReservationUpcoming,
  reservationTimestamp,
  resolvePhotoAction,
  resolvePhotoRoute,
  resolveReservationThumbnail,
  sortReservationsByProximity,
} from "@/util/mypageReservationPresentation"
import {
  refundPolicyProductType,
  resolveReservationServiceKey,
  usesDirectPhotoDelivery,
} from "@/services/reservationService"

// 손님이 직접 시간을 바꿀 수 있는 마지막 날: 방문 4일 전(서버 reservationService의 TIME_CHANGE_SELF_MIN_DAYS와 같은 값)
const TIME_CHANGE_MIN_DAYS = 4
// 예약 상세에서 '시간 변경' 단추 대신 보이는 안내 글
const CLOSED_NOTE_KEYS = {
  cancel: "page.mypage.reserv.cancel-unavailable",
  window: "page.mypage.reserv.time-change.blocked-window",
  limit: "page.mypage.reserv.time-change.blocked-limit",
}

export default {
  components: {
    PhotoDeliveryGuide,
    MagicCodes,
    CautionMesageDialog,
    ReservationTimeChange,
  },
  data: () => ({
    list: [],
    loadState: "loading",

    selectedItem: {},
    detailItem: null,
    timeChangeOpen: false,

    dialog: {
      cancel: false,
      detail: false,
    },
    cancelComponentKey: 0,
    reservation_canceling: false,

    show_refund_policy: false,
    refund_policy_dialog_componentKey: 0,
    refundPolicyProductType: "makershot",

    individualPromotions: [],
    allPromotions: [],
    conceptList: [],
    reservationImage,
    statusNow: Date.now(),
    statusClockTimer: null,
  }),

  async mounted() {
    // 예약 목록 불러오기
    await Promise.all([
      this.loadAllPromotions(),
      this.loadIndividualPromotions(),
      this.loadConceptCatalog(),
    ])
    this.load()
    this.statusClockTimer = window.setInterval(() => {
      this.statusNow = Date.now()
    }, 60000)
  },

  beforeDestroy() {
    if (this.statusClockTimer) {
      window.clearInterval(this.statusClockTimer)
    }
  },

  computed: {
    ...mapState(["lang"]),
    domLanguage() {
      return this.lang === "jp" ? "ja" : this.lang
    },
    instagramDmUrl() {
      return INSTAGRAM_DM_URL
    },
    // 메이커샷 촬영은 방문 시간에서 이만큼(분) 뒤다
    shootingOffset() {
      return SHOOTING_TIME
    },
    sortedReservations() {
      return [...this.list].sort((a, b) => this.getReservationTimestamp(a) - this.getReservationTimestamp(b))
    },
    upcomingReservation() {
      return findUpcomingReservation(
        this.sortedReservations,
        this.statusNow,
        (item) => this.isCanceled(item)
      )
    },
    otherReservations() {
      return sortReservationsByProximity(
        this.list.filter((item) => item !== this.upcomingReservation),
        this.statusNow
      )
    },
  },

  methods: {
    ...mapActions(["loadConceptsAll"]),

    getReservationTimestamp(item) {
      return reservationTimestamp(item)
    },

    hasReservationStarted(item) {
      const reservationTimestamp = this.getReservationTimestamp(item)
      return Number.isFinite(reservationTimestamp) && this.statusNow >= reservationTimestamp
    },

    formatReservationDate(item) {
      return `${item.year}.${item.month}.${item.day} / ${item.times}`
    },

    getReservationStatusText(item) {
      if (this.isCanceled(item)) {
        return this.$t("page.mypage.reserv.cancel-complete")
      }
      if (isReservationUpcoming(item, this.statusNow)) {
        return this.$t("page.mypage.reserv.shooting-scheduled")
      }
      return this.$t("page.mypage.reserv.shooting-complete")
    },

    getReservationThumbnail(item) {
      const product = this.getProductOpiton(item)
      const thumbnail = resolveReservationThumbnail({
        item,
        concept: this.getConceptRecord(item),
        product,
        fallback: this.reservationImage,
      })

      if (
        typeof window !== "undefined" &&
        window.location.hostname === "127.0.0.1" &&
        String(thumbnail).startsWith("/upload/")
      ) {
        return `/api/__preview_asset?path=${encodeURIComponent(thumbnail)}`
      }

      return thumbnail
    },

    // 쓴 횟수 답을 받은 뒤 연다: 연 다음에 답이 오면 문의 문장·단추가 붙어 '영수증 보기' 자리가 바뀐다(검수 11회).
    // 아직 묻는 중이면 그 답을, 묻기에 실패했으면 한 번 더 물은 답을 기다린다(요청마다 5초 제한).
    async openReservationDetail(item) {
      if (this.isTimeChangeTarget(item) && !(item.timeChangeChecked === true && item.timeChangeUsedUp !== null)) {
        await this.checkTimeChangeUsed(item)
      }
      this.detailItem = item
      this.timeChangeOpen = false
      this.dialog.detail = true
    },

    // 쓴 횟수는 목록에 오지 않는다. 날짜로 보아 바꿀 수 있는 예약만, 목록을 그린 뒤 서버에 규칙만 가볍게 물어
    // 이미 바꾼 예약이면 '시간 변경' 단추 대신 안내를 보인다. 답이 오기 전에는 단추를 그리지 않는다(떴다 사라지지 않게).
    // 묻지 못하면 단추를 보이고, 창을 열 때 서버가 다시 알려 준다.
    // 방문 3일 안쪽 예약도 묻는다: 이미 한 번 바꾼 손님에게 3일 전 안내('급한 사정이면 DM')를 보이면
    // 한 번 더 바꿔 준다고 읽힌다(본주소 전 코드 검사 10/11). 쓴 횟수는 까닭(reason)과 상관없이 숫자로 본다.
    // 같은 예약을 동시에 두 번 묻지 않게 묻는 중인 약속(Promise)을 예약에 붙여 둔다(화면에 쓰지 않는 값이라 반응형으로 두지 않음).
    checkTimeChangeUsed(item) {
      if (!this.isTimeChangeTarget(item)) return Promise.resolve()
      const failed = item.timeChangeChecked === true && item.timeChangeUsedUp === null
      if (item._timeChangeCheck && !failed) return item._timeChangeCheck
      item._timeChangeCheck = this.askTimeChangeUsed(item)
      return item._timeChangeCheck
    },

    async askTimeChangeUsed(item) {
      let usedUp = false
      try {
        const { data } = await this.$http.get(`/api/booking/reservations/${item.reservation_id}/time-options`, { params: { rule: "only" }, timeout: 5000 })
        const rule = data && data.rule
        usedUp = Boolean(rule && Number(rule.maxChanges) > 0 && Number(rule.usedChanges) >= Number(rule.maxChanges))
      } catch (error) {
        // 묻지 못하면 '모름'(null): 단추는 두되(서버가 다시 막는다) 문의 문장·단추는 보이지 않는다
        usedUp = null
      }
      this.$set(item, "timeChangeUsedUp", usedUp)
      this.$set(item, "timeChangeChecked", true)
    },

    // 날짜로 보아 손님이 직접 바꿀 수 있는 예약: 결제를 마친 방문 전 예약이고 방문 4일 전까지
    mayChangeTimeByDate(item) {
      return this.isTimeChangeTarget(item) && this.getDiffDays(item.year, item.month, item.day) >= TIME_CHANGE_MIN_DAYS
    },

    // 시간 변경을 다루는 예약인가: 결제가 끝났고 아직 방문 전인 예약
    isTimeChangeTarget(item) {
      if (!item || item.is_fixture || !item.reservation_id || item.status !== "결제완료") return false
      const time = String(item.times || "").split(",")[0]
      return Boolean(time) && !this.isDatePassed(item.year, item.month, item.day, time)
    },

    // 손님이 직접 시간을 바꿀 수 있는가: 방문 4일 전까지(3일 전부터는 매장에서만 바꾼다).
    // 쓴 횟수는 목록을 그린 뒤 서버에 물어 둔다(checkTimeChangeUsed). 답이 온 뒤에만 단추를 보인다.
    canChangeTime(item) {
      return this.mayChangeTimeByDate(item) && item.timeChangeChecked === true && item.timeChangeUsedUp !== true
    },

    // 취소·시간 변경 단추가 안 보일 때 그 까닭을 한 줄로 알려 준다.
    // 방금 결제해 취소는 되지만 방문이 3일 안쪽이면 시간 변경만 닫혀 있다.
    // 단추 대신 보일 안내의 종류: 취소 기간이 지남(cancel), 방문 3일 전부터(window), 횟수를 다 씀(limit)
    // 횟수는 목록을 받을 때 서버에 물어 알거나, 바꾼 직후에 안다.
    // 횟수를 다 쓴 예약은 방문 3일 안쪽이어도 'limit'(문의 단추 없음)을 먼저 보인다.
    getChangeClosedKind(item) {
      const target = this.isTimeChangeTarget(item)
      // 이미 바꾼 예약: 3일 안쪽이면 취소 안내의 첫 문장만(closedNoteLines), 아니면 '한 번' 안내
      if (target && item.timeChangeUsedUp === true) return this.isCancelWindowClosed(item) ? "cancel" : "limit"
      if (this.isCancelWindowClosed(item)) return "cancel"
      if (!target) return ""
      if (this.getDiffDays(item.year, item.month, item.day) < TIME_CHANGE_MIN_DAYS) return "window"
      return ""
    },

    getChangeClosedNote(item) {
      const key = CLOSED_NOTE_KEYS[this.getChangeClosedKind(item)]
      return key ? this.$t(key) : ""
    },

    // 문의 단추는 '3일 전부터는 직원이 옮긴다'는 길(cancel, window)에만 둔다.
    // 횟수를 다 쓴 안내(limit)에는 두지 않는다(회장 10/9: 말하면 또 바꿔 준다고 읽히면 안 됨). 시간 변경 창의 NO_ASK_REASONS와 같은 규칙이다.
    // '아직 안 바꿈'이 서버 답으로 확인된 예약에만 둔다(답 전·묻기 실패·이미 바꿈에는 두지 않음, 검수 10회).
    closedNoteAsksStore(item) {
      return ["cancel", "window"].includes(this.getChangeClosedKind(item)) && item.timeChangeChecked === true && item.timeChangeUsedUp === false
    },

    // 문의 단추가 없으면 3일 전 안내도 문의 문장 없이 첫 문장(취소·환불·시간 변경이 안 됨)만 보인다.
    closedNoteLines(item) {
      const lines = this.noteLines(this.getChangeClosedNote(item))
      const kind = this.getChangeClosedKind(item)
      return ["cancel", "window"].includes(kind) && !this.closedNoteAsksStore(item) ? lines.slice(0, 1) : lines
    },

    // 시간이 바뀌면 목록과 상세에 바로 반영한다.
    onTimeChanged(changed) {
      if (!this.detailItem || !changed) return
      this.detailItem.times = changed.time
      if (changed.maker) this.detailItem.maker = changed.maker
      const left = Number(changed.maxChanges) - Number(changed.usedChanges)
      if (Number.isFinite(left) && left <= 0) this.onTimeChangeLimit()
    },

    noteLines(text) {
      // 가운뎃점(취소·환불) 앞뒤에서는 줄이 갈리지 않게 낱말 이음표를 넣는다
      return (String(text || "").match(/[^.!?。]+[.!?。]?/g) || []).map((line) => line.trim().replace(/\s*·\s*/g, "\u2060·\u2060")).filter(Boolean)
    },

    // 남은 횟수가 없는 예약: '시간 변경' 단추를 거두고 한 줄 안내로 바꾼다
    onTimeChangeLimit() {
      if (this.detailItem) this.$set(this.detailItem, "timeChangeUsedUp", true)
    },

    // 창을 닫으면 창을 열었던 '시간 변경' 단추로 초점을 돌려준다(사파리는 누른 단추에 초점을 두지 않아 직접 돌려준다).
    closeTimeChange() {
      this.timeChangeOpen = false
      this.$nextTick(() => {
        const target = this.$refs.timeChangeButton || this.$refs.closedNote
        if (target) target.focus()
      })
    },

    openDetailCancel(item) {
      this.dialog.detail = false
      this.reservationCancelOpen(item)
    },

    newReservation() {
      this.$router.push("/")
    },

    // 예약 목록 불러오기
    load() {
      if (!this.list.length) this.loadState = "loading"
      return Promise.all([
        this.$http.post("/api/order/select/specific/user", {
          params: {
            user_id: this.$store.state.lafia_user.user_id,
          },
        }),
        this.loadReservationFixtures(),
      ]).then(async ([res, fixtures]) => {
          const reservations = [
            ...(Array.isArray(res.data) ? res.data : []),
            ...fixtures,
          ]
          this.list = await Promise.all(
            reservations.map(async (item) => {
              if (item.is_fixture) {
                item.photoLookupStatus = "ready"
                if (this.hasDirectPhotoDelivery(item)) {
                  item.photoDeliveryStatus = "not_ready"
                } else {
                  item.hasMyShots = false
                }
                return item
              }
              try {
                if (!this.hasDirectPhotoDelivery(item)) {
                  const { hasMyShots, qrcodeId } = await this.getHasMyShotsByReservation(item.reservation_id)
                  item.hasMyShots = hasMyShots
                  item.qrcodeId = qrcodeId
                  item.photoLookupStatus = "ready"
                } else {
                  const delivery = await this.getPhotoDeliveryByReservation(item.reservation_id)
                  item.photoDelivery = delivery
                  item.photoDeliveryStatus = delivery ? delivery.status : "not_ready"
                  item.photoLookupStatus = "ready"
                }
              } catch (error) {
                item.photoLookupStatus = "error"
                if (!this.hasDirectPhotoDelivery(item)) {
                  item.hasMyShots = false
                } else {
                  item.photoDelivery = null
                  item.photoDeliveryStatus = "not_ready"
                }
              }
              return item
            })
          )
          this.loadState = "ready"
          this.list.forEach((item) => this.checkTimeChangeUsed(item))
      }).catch((error) => {
        console.error("Error loading reservations:", error)
        this.loadState = "error"
      })
    },

    async loadReservationFixtures() {
      const fixtureUrl =
        process.env.VUE_APP_MYPAGE_CANARY_FIXTURE_URL || ""
      if (!fixtureUrl) {
        return []
      }

      try {
        const response = await this.$http.get(fixtureUrl, {
          headers: {
            "Cache-Control": "no-cache",
          },
        })
        return response.data && Array.isArray(response.data.reservations)
          ? response.data.reservations
          : []
      } catch (error) {
        console.error("Error loading reservation fixtures:", error)
        return []
      }
    },

    async loadConceptCatalog() {
      try {
        this.conceptList = await this.loadConceptsAll()
      } catch (error) {
        console.error("Error getting concepts:", error)
        this.conceptList = []
      }
    },

    getConceptRecord(item) {
      if (!item) return null

      const conceptId = item.conceptId ?? item.concept_id
      if (conceptId !== undefined && conceptId !== null) {
        const byId = this.conceptList.find(
          (concept) => String(concept.id) === String(conceptId)
        )
        if (byId) return byId
      }

      const title = String(item.concept || "").trim()
      const productType = item.productType || item.product_type
      return (
        this.conceptList.find(
          (concept) =>
            concept.title === title &&
            (!productType || concept.productType === productType)
        ) ||
        this.conceptList.find((concept) => concept.title === title) ||
        null
      )
    },

    getConceptTitle(item) {
      const concept = this.getConceptRecord(item)
      if (!concept) return item?.concept || ""

      if (this.lang === "en") {
        return concept.title_en || concept.title
      }
      if (this.lang === "jp") {
        return concept.title_jp || concept.title
      }
      return concept.title
    },

    // 예약취소 열기
    reservationCancelOpen(item) {
      this.cancelComponentKey++
      this.selectedItem = item
      this.dialog.cancel = true
    },

    // 예약취소 제출
    async reservationCancelSubmit(item) {
      // console.log(item)
      // console.log(this.hoursDifference(item.created))

      if (this.isDatePassed(item.year, item.month, item.day, item.times.split(",")[0])) {
        alert(this.$t("alert.error_reservation_cancel_failed"))
        return
      }

      const canAmount = this.getCanCancelAmount(item)
      if (canAmount < 0) {
        alert(this.$t("alert.cancel-amount-invalid"))
        return
      }

      try {
        this.reservation_canceling = true
        // 시간 체크
        /*
        if (canAmount === 0 && !item.paymentKey) {
          const response = await this.$http.post("/api/order/update/status", {
            params: {
              id: item.id,
              status: "취소완료",
            },
          })
          if (response.data.affectedRows) {
            alert("예약이 정상적으로 취소되었습니다.")
            this.load()
            this.dialog.cancel = false
          }
          this.$http
            .post("/api/coupon/cancelUseCoupon", {
              toss_id: item.toss_id.toString(),
              user_id: this.$store.state.lafia_user.user_id,
            })
            .then((res) => {
            })
        } else { */

        let params = {
          orderListId: item.id,
          paymentKey: item.paymentKey,
          cancelAmount: canAmount,
          cancelReason: "사용자 취소",
          isAdmin: false,
          isAll: item.pay_amount === canAmount,
        }


        const orderFunc = (params) => (item.payment_type === "payletter" ? payletterService.cancelOrder(params) : this.$http.post("/api/payments/toss/order/cancel", params))

        const response = await orderFunc(params)
        /*
        const response = await this.$http.post("/api/payments/toss/order/cancel", {
          orderListId: item.id,
          paymentKey: item.paymentKey,
          cancelAmount: canAmount,
          cancelReason: "사용자 취소",
          isAdmin: false,
          isAll: item.pay_amount === canAmount,
        }) */

        //if (response.data.orderId) {
        this.$http.post("/api/coupon/cancelUseCoupon", {
          toss_id: item.toss_id.toString(),
          user_id: this.$store.state.lafia_user.user_id,
        })

        alert(this.$t("alert.success_to_cancel_reservation"))
        this.load()
        this.dialog.cancel = false
        //}
        //}
      } catch (e) {
        // 서버 안내가 있으면 그대로 보여 주고, 목록만 다시 불러온다(페이지 전체 새로고침 금지)
        const message = e && e.response && e.response.data && e.response.data.message
        alert(message ? this.$t("alert.error_contact_mananger_with_message", { message }) : this.$t("alert.fail_to_cancel_reservation"))
        this.dialog.cancel = false
        this.load()
      } finally {
        this.reservation_canceling = false
      }
    },

    // 촬영 3일 전부터 취소 버튼이 사라지는 경우에만 이유를 보여 준다(촬영이 끝난 예약은 제외)
    isCancelWindowClosed(item) {
      if (!item || item.is_fixture || this.isCanceled(item)) return false
      const time = String(item.times || "").split(",")[0]
      if (!time || this.isDatePassed(item.year, item.month, item.day, time)) return false
      return this.isCantReserveCancel(item.created, item.year, item.month, item.day, time)
    },

    isCantReserveCancel(created, year, month, day, time) {
      return !this.isWithin24Hours(created) && (this.isDatePassed(year, month, day, time) || this.getDiffDays(year, month, day) <= 3)
    },

    // 현재 시각을 지났는지 체크
    isDatePassed(year, month, day, time) {
      const inputDate = new Date(Date.UTC(year, month - 1, day, ...time.split(":").map(Number)) - 9 * 3600000)
      return inputDate < new Date()
    },

    hoursDifference(created) {
      const target = this.$DateTime.fromISO(created)
      const now = this.$DateTime.now()
      const diff = now.diff(target).as("hours")
      return Math.ceil(Math.abs(diff))
    },

    // X일 이내 체크용 -예약일 기준으로
    isWithinDiffHours(year, month, day, hour, diff) {
      const difference = this.getDiffHours(year, month, day, hour)
      return Math.ceil(Math.abs(difference)) <= diff
    },

    getDiffHours(year, month, day, hour) {
      const targetDate = this.$DateTime.local(year, month, day, hour)
      const now = this.$DateTime.now()
      const difference = now.diff(targetDate).as("hours")
      return difference
    },

    getDiffDays(year, month, day) {
      return Math.floor(Date.UTC(year, month - 1, day) / 86400000) - Math.floor((Date.now() + 9 * 3600000) / 86400000)
    },

    isWithin24Hours(created) {
      const target = this.$DateTime.fromISO(created)
      const now = this.$DateTime.now()

      const diff = now.diff(target).as("hours")
      return Math.abs(diff) <= 24
    },

    getCanCancelAmount(item) {
      const amount = Number(item.pay_amount)
      const balance = Number(item.balance_amount)
      if (item.balance_amount == null || !Number.isSafeInteger(balance) || balance < 0 || balance > amount) return -1
      const remainingRefund = rate => Math.max(0, amount * rate - (amount - balance))
      const diffDay = this.getDiffDays(item.year, item.month, item.day)
      if (this.isDatePassed(item.year, item.month, item.day, item.times.split(",")[0])) {
        // 환불 불가
        return -1
      } else if (this.isWithin24Hours(item.created)) {
        // 24시간 이내 전액 환불
        return remainingRefund(1)
      } else if (diffDay <= 3) {
        return -1
      } else if (diffDay <= 6) {
        // 4 ~ 6
        const rate = [0.3, 0.5, 0.8]
        return remainingRefund(rate[diffDay - 4])
      } else {
        // 전액 환불
        return remainingRefund(1)
      }
    },

    getTicketTitle(item) {
      // memo 필드에서 productId 추출 (JSON 또는 문자열)
      let productId = item.memo
      try {
        const memoObj = JSON.parse(item.memo)
        if (memoObj && memoObj.id) {
          productId = memoObj.id
        }
      } catch (e) {
        // JSON 파싱 실패 시 기존 문자열 그대로 사용
      }

      // item.promotionCode 가 있으면 그걸로 타이틀 가져오기 없으면 기본 타이틀로 보여주기
      if (item.promotionCode) {
        const promotion = this.allPromotions.find((p) => p.code.toLowerCase() === item.promotionCode.toLowerCase())
        if (promotion && promotion.products) {
          const products = JSON.parse(promotion.products)
          const product = products.find((p) => p.id === productId)
          if (product) {
            return this.getProductTitle(product)
          }
        }
      }

      const defaultPromotion = this.allPromotions.find((p) => p.code === "default")
      if (defaultPromotion && defaultPromotion.products) {
        const products = JSON.parse(defaultPromotion.products)
        const product = products.find((p) => p.id === productId)
        if (product) {
          return this.getProductTitle(product)
        }
      }
      return this.$t("page.mypage.reserv.ticket-title")
    },

    getServiceTitle(item) {
      const serviceKey = this.getServiceKey(item)
      if (!serviceKey) {
        return this.getTicketTitle(item)
      }

      const translationKey = `page.mypage.reserv.service.${serviceKey}`
      const translated = this.$t(translationKey)
      return translated === translationKey ? this.getTicketTitle(item) : translated
    },

    getServiceKey(item) {
      return resolveReservationServiceKey(item, this.allPromotions)
    },

    hasDirectPhotoDelivery(item) {
      return usesDirectPhotoDelivery(item, this.allPromotions)
    },

    getRefundPolicyProductType(item) {
      return refundPolicyProductType(item, this.allPromotions)
    },

    getProductOpiton(item) {
      // memo 필드에서 productId 추출 (JSON 또는 문자열)
      let productId = item.memo
      try {
        const memoObj = JSON.parse(item.memo)
        if (memoObj && memoObj.id) {
          productId = memoObj.id
        }
      } catch (e) {
        // JSON 파싱 실패 시 기존 문자열 그대로 사용
      }

      var promotion = null
      if (item.promotionCode) {
        promotion = this.allPromotions.find((p) => p.code.toLowerCase() == item.promotionCode.toLowerCase())
      }
      if (!promotion) {
        promotion = this.allPromotions.find((p) => p.code === "default")
      }
      if (promotion && promotion.products) {
        const products = JSON.parse(promotion.products)
        const product = products.find((p) => p.id === productId)
        return product
      }
      return null
    },

    getProductTitle(product) {
      if (!product || !product.title) return ""
      switch (this.lang) {
        case "en":
          return product.title.en || product.title.kr || ""
        case "jp":
          return product.title.jp || product.title.kr || ""
        default:
          return product.title.kr || ""
      }
    },

    getSelectedProductTitle(item) {
      return this.getProductTitle(this.getProductOpiton(item))
    },

    getPersonCount(item) {
      const count = Number(item.cloth)
      return Number.isFinite(count) && count > 0 ? count : 1
    },

    getReservationImagePosition() {
      return "50% 50%"
    },

    isTossPayment(item) {
      return Boolean(item.paymentKey) && item.payment_type !== "payletter"
    },

    isCanceled(item) {
      return item.status === "취소완료" || item.status === "취소"
    },

    getPaymentStatusText(item) {
      if (this.isCanceled(item)) {
        return this.$t("page.mypage.reserv.cancel-complete")
      }
      if (item.status === "취소요청") {
        return this.$t("page.mypage.reserv.cancel-pending")
      }
      return this.$t("page.mypage.reserv.payment-complete")
    },

    getCancelHistory(item) {
      if (Array.isArray(item.cancels)) {
        return item.cancels
      }
      if (typeof item.cancels === "string" && item.cancels.trim()) {
        try {
          const parsed = JSON.parse(item.cancels)
          return Array.isArray(parsed) ? parsed : []
        } catch (_error) {
          return []
        }
      }
      return []
    },

    getCancelAmount(item) {
      const directAmount = Number(item.cancel_amount)
      if (Number.isFinite(directAmount) && directAmount > 0) {
        return directAmount
      }
      return this.getCancelHistory(item).reduce((total, cancel) => {
        const amount = Number(cancel.cancelAmount)
        return total + (Number.isFinite(amount) ? amount : 0)
      }, 0)
    },

    getCancelDate(item) {
      const canceledAt = item.canceled_at || this.getCancelHistory(item)[0]?.canceledAt
      if (!canceledAt) {
        return ""
      }
      const date = new Date(canceledAt)
      if (Number.isNaN(date.getTime())) {
        return canceledAt
      }
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      const hours = String(date.getHours()).padStart(2, "0")
      const minutes = String(date.getMinutes()).padStart(2, "0")
      return `${year}.${month}.${day} / ${hours}:${minutes}`
    },

    getRefundDialogMessage(item) {
      const { year, month, day, created } = item
      const diffDay = this.getDiffDays(year, month, day)

      if (this.isWithin24Hours(created)) {
        return this.$t("dialog.reservation-cancel-confirm-24")
      }
      // 시간만 바꾸는 길은 실제로 시간을 바꿀 수 있는 예약에만 알려 준다.
      const hint = this.canChangeTime(item) ? this.$t("dialog.reservation-cancel-time-hint") : ""
      if (diffDay <= 6) {
        // 4 ~ 6
        const rate = ["30", "50", "80"]
        return this.$t("dialog.reservation-cancel-confirm-day", { day: diffDay, percent: rate[diffDay - 4], hint })
      }
      return this.$t("dialog.reservation-cancel-confirm", { hint })
    },

    getShootingTime(time) {
      const [hours, minutes] = time.split(":").map(Number)
      const totalMinutes = hours * 60 + minutes + SHOOTING_TIME
      const endHours = Math.floor(totalMinutes / 60)
      const endMinutes = totalMinutes % 60
      const shootingtime = `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`
      return shootingtime
    },

    openCautionDialog(item) {
      this.refundPolicyProductType = this.getRefundPolicyProductType(item)
      this.show_refund_policy = true
      this.refund_policy_dialog_componentKey++
    },

    // 예약 상세 > 촬영 이미지 존재 여부 체크
    async getHasMyShotsByReservation(reservation_id) {
      const response = await this.$http.post("/api/qrcode/select/byReservation", {
        params: {
          user_id: this.$store.state.lafia_user.user_id,
          reservation_id: reservation_id,
        },
      })
      const qrCodeItem = response.data[0]
      if (qrCodeItem) {
        return {
          qrcodeId: qrCodeItem.id,
          hasMyShots: response.data.length > 0,
        }
      } else {
        return {
          hasMyShots: false,
        }
      }
    },

    async getPhotoDeliveryByReservation(reservation_id) {
      try {
        const response = await this.$http.get(
          `/api/photo-deliveries/reservations/${reservation_id}`
        )
        return response.data.delivery || null
      } catch (error) {
        if (error.response && error.response.status === 404) {
          return null
        }
        throw error
      }
    },

    isPhotoActionDisabled(item) {
      return resolvePhotoAction({
        item,
        serviceKey: this.getServiceKey(item),
        now: this.statusNow,
      }).disabled
    },

    getPhotoActionLabel(item) {
      return this.$t(
        resolvePhotoAction({
          item,
          serviceKey: this.getServiceKey(item),
          now: this.statusNow,
        }).labelKey
      )
    },

    async openReceipt(item) {
      if (item.receipt_unavailable_message) {
        alert(item.receipt_unavailable_message)
        return
      }
      if (!item.paymentKey) {
        alert(this.$t("alert.receipt-not-found"))
        return
      }
      const popup = window.open("about:blank", "_blank")
      try {
        const res = await this.$http.post("/api/payments/toss/receipt", {
          paymentKey: item.paymentKey,
        })
        const url = res.data && res.data.receipt
        if (!url) {
          if (popup) popup.close()
          alert(this.$t("alert.receipt-not-found"))
          return
        }
        if (popup) {
          popup.location.href = url
        } else {
          window.location.href = url
        }
      } catch (e) {
        if (popup) popup.close()
        alert(this.$t("alert.receipt-not-found"))
      }
    },

    onClickCheckOriginal(item) {
      const action = resolvePhotoAction({
        item,
        serviceKey: this.getServiceKey(item),
        now: this.statusNow,
      })
      const route = resolvePhotoRoute({ action, item })
      if (route) {
        this.$router.push(route)
      } else {
        alert(this.$t("alert.error_page"))
      }
    },

    async loadIndividualPromotions() {
      try {
        const response = await this.$http.get("/api/promotion/select/individual")
        this.individualPromotions = response.data
      } catch (e) {
        console.error("Error getting individual promotions:", e)
      }
    },

    async loadAllPromotions() {
      try {
        const response = await this.$http.post("/api/promotion/select/getAllProductsInfo")
        this.allPromotions = response.data
      } catch (e) {
        console.error("Error getting all promotions:", e)
      }
    },

    getIndividualPromotionName(code) {
      if (this.lang && this.lang !== "ko") return code
      const promotion = this.individualPromotions.find((p) => p.code.toLowerCase() === code.toLowerCase())
      return promotion ? promotion.name : code
    },

    translateOptionTitle(title) {
      const dict = {
        보정추가: { en: "Retouch", jp: "リタッチ" },
        "보정 추가": { en: "Retouch", jp: "リタッチ" },
        "컨셉추가(솔로)": { en: "concept(solo)", jp: "コンセプト(ソロ)" },
        "컨셉추가(그룹)": { en: "concept(group)", jp: "コンセプト(グループ)" },
        "로고삭제 파일 (상업용 허가)": { en: "Logo removal (commercial)", jp: "ロゴ削除ファイル（商用許可）" },
        "족자 굿즈": { en: "Scroll Goods", jp: "掛け軸グッズ" },
        "패턴 추가": { en: "Add Pattern", jp: "パターン追加" },
      }
      if (!this.lang || this.lang === "ko") return title
      let result = title
      for (const [kr, trans] of Object.entries(dict)) {
        if (result.includes(kr)) {
          result = result.replace(kr, trans[this.lang] || kr)
        }
      }
      return result
    },
    getSelectedOptionTitles(item) {
      const savedOptions = item && item.options
      if (!savedOptions) {
        return []
      }

      let options = savedOptions
      try {
        if (typeof savedOptions === "string") {
          if (savedOptions.trim() === "") return []
          options = JSON.parse(savedOptions)
        }
      } catch (error) {
        console.error("Error parsing options:", error)
        return []
      }

      if (!Array.isArray(options)) return []

      return options
        .map((option) => {
          if (!option || !option.title) return ""

          let title = option.title
          if (typeof title === "object") {
            title = title[this.lang] || title.kr || title.en || title.jp || ""
          }

          return this.translateOptionTitle(String(title).trim())
        })
        .filter((title) => title)
    },
  },
}
</script>
<style scoped>
.quiet-schedule {
  min-height: 100%;
  color: #5e4440;
  background: #fffdf9;
  --mypage-font-body: "NanumSquare", sans-serif;
  --mypage-font-display: "Cafe24Danjunghae", serif;
  font-family: var(--mypage-font-body);
}

.quiet-schedule:lang(ja) {
  --mypage-font-body: "Noto Sans JP", sans-serif;
  --mypage-font-display: "Noto Sans JP", sans-serif;
}

.quiet-schedule:lang(ja) .schedule-heading__title {
  font-family: var(--mypage-font-display) !important;
}

.quiet-schedule__body {
  width: min(100%, 920px);
  padding: 0 clamp(18px, 7.2vw, 42px);
  background: transparent !important;
}

.upcoming-reservation {
  display: grid;
  grid-template-columns: clamp(104px, 16vw, 122px) minmax(0, 1fr);
  gap: 14px;
  min-height: 148px;
  overflow: hidden;
  padding: 10px;
  border: 1px solid rgba(203, 170, 107, 0.82);
  border-radius: 15px;
  background: #fffaf2;
  box-shadow: 0 10px 26px rgba(91, 63, 58, 0.09);
}

.upcoming-reservation__photo {
  width: 100%;
  min-height: 128px;
  aspect-ratio: 4 / 5;
  align-self: stretch;
  overflow: hidden;
  border: 1px solid rgba(190, 160, 104, 0.48);
  border-radius: 11px;
  background-color: #ddd2cc;
  background-repeat: no-repeat;
  background-size: cover;
}

.upcoming-reservation__content {
  display: grid;
  min-width: 0;
  grid-template-rows: minmax(68px, 1fr) 40px;
  gap: 8px;
  padding: 2px 2px 2px 0;
}

.upcoming-reservation__summary {
  display: grid;
  min-width: 0;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto 20px;
  align-items: center;
  gap: 8px;
  padding: 3px 0 7px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.upcoming-reservation__summary:focus-visible {
  border-radius: 7px;
  outline: 2px solid rgba(215, 67, 105, 0.45);
  outline-offset: 2px;
}

.upcoming-reservation__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
  container-name: upcoming-copy;
  container-type: inline-size;
}

.upcoming-reservation__title {
  overflow: hidden;
  color: #5f4844;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@container upcoming-copy (max-width: 182px) {
  .upcoming-reservation__title {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    -webkit-line-clamp: unset;
  }

  .upcoming-reservation__separator {
    display: none;
  }

  .upcoming-reservation__service,
  .upcoming-reservation__concept {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.upcoming-reservation__date {
  color: #8e7d78;
  font-family: "Times New Roman", serif;
  font-size: 15px;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.upcoming-reservation__status {
  color: #c64064;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.upcoming-reservation__chevron {
  color: #b8a69f !important;
}

.reservation-detail__status {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 4px 10px;
  border: 1px solid rgba(217, 75, 107, 0.32);
  border-radius: 999px;
  color: #c64064;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.upcoming-reservation__actions {
  display: grid;
  min-width: 0;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-self: end;
  gap: 8px;
}

.upcoming-reservation__actions .reservation-card-action.v-btn {
  width: 100%;
  height: 40px !important;
}

.reservation-card-action.v-btn {
  min-width: 0 !important;
  height: 38px !important;
  padding: 0 10px !important;
  border-radius: 7px;
  font-size: 12px;
  letter-spacing: -0.02em;
  box-shadow: none;
}

.reservation-card-action--notice.v-btn {
  border: 1px solid #ddd8d5;
  background: #fff !important;
  color: #6e615d !important;
}

.reservation-card-action--original.v-btn {
  border: 1px solid #d74369;
  background: #d74369 !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(215, 67, 105, 0.22);
}

.reservation-card-action--original.v-btn:not(.v-btn--disabled):hover,
.reservation-card-action--original.v-btn:not(.v-btn--disabled):focus-visible {
  border-color: #c13459;
  background: #c13459 !important;
}

.reservation-card-action--original.v-btn.v-btn--disabled {
  border-color: rgba(179, 166, 161, 0.35) !important;
  background: #f2efed !important;
  color: rgba(110, 91, 85, 0.42) !important;
  box-shadow: none;
}

.upcoming-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px 20px;
  border: 1px solid rgba(203, 170, 107, 0.55);
  border-radius: 22px;
  background: #fff9ee;
  text-align: center;
}

.list-state__text {
  margin: 12px 0 16px;
  color: #6b4b47;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
}
/* 취소·시간 변경이 닫힌 까닭과 문의 단추: 아래 단추들 바로 위에 둔다 */
.reservation-detail__closed {
  display: grid;
  gap: 10px;
  margin: 16px 24px 0;
  outline: none;
}

.reservation-detail__cancel-note {
  margin: 0;
  color: #705552;
  font-size: 13px;
  line-height: 1.6;
  text-align: center;
  text-wrap: balance;
  word-break: keep-all;
}

/* 일본어는 keep-all이면 줄이 바뀌지 않아 창 밖으로 넘친다. 되는 브라우저에서는 말 마디에서 줄을 바꾼다. */
.reservation-detail__cancel-note:lang(ja) {
  word-break: normal;
  line-break: strict;
}

@supports (word-break: auto-phrase) {
  .reservation-detail__cancel-note:lang(ja) {
    word-break: auto-phrase;
  }
}

.reservation-detail__cancel-note span {
  display: block;
}

/* 인스타그램 DM 단추: 시간 변경 창의 것과 같은 모양(포인트 색 테두리), 누르는 자리 44px */
.reservation-detail__dm {
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 24px;
  border: 1px solid #c64064;
  border-radius: 7px;
  background: #fff;
  color: #c64064;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-decoration: none;
  transition: background-color 160ms ease-out, transform 160ms ease-out;
}

.reservation-detail__dm .v-icon {
  color: inherit;
}

.reservation-detail__dm:active {
  transform: scale(0.97);
}

.reservation-detail__dm:focus-visible {
  outline: 2px solid #684945;
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .reservation-detail__dm:hover {
    background: #fffaf2;
  }
}

/* 예약 취소 창 글: 낱말 중간에서 줄이 갈리지 않게 하고, 끝에 한 낱말만 남지 않게 줄 길이를 고르게 한다
   (일본어는 말 마디에서 바꾼다). */
.reservation-cancel__message {
  word-break: keep-all;
  text-wrap: balance;
}

.reservation-cancel__message:lang(ja) {
  word-break: normal;
  line-break: strict;
}

@supports (word-break: auto-phrase) {
  .reservation-cancel__message:lang(ja) {
    word-break: auto-phrase;
  }
}
.upcoming-empty__title {
  margin: 0;
  color: #6b4b47;
  font-size: 24px;
  font-weight: 400;
}

.upcoming-empty__button.v-btn {
  min-width: 170px;
  height: 44px;
  margin-top: 14px;
  border-radius: 7px;
  background: #d74369 !important;
  color: #fff !important;
  box-shadow: none;
}

.other-reservations {
  margin-top: 20px;
}

.other-reservations__heading {
  display: flex;
  align-items: baseline;
  gap: 9px;
  margin-bottom: 8px;
}

.other-reservations__heading h3 {
  margin: 0;
  color: #6b4b47;
  font-size: 26px;
  font-weight: 400;
}

.other-reservations__heading span {
  color: #bd9b5c;
  font-size: 13px;
  font-weight: 700;
}

.other-reservations__list {
  display: grid;
  gap: 12px;
}

.other-reservation-card {
  display: grid;
  width: 100%;
  min-height: 112px;
  grid-template-columns: 80px minmax(0, 1fr);
  grid-template-rows: minmax(54px, auto) 34px;
  column-gap: 14px;
  overflow: hidden;
  padding: 8px 10px 8px 8px;
  border: 1px solid rgba(203, 170, 107, 0.78);
  border-radius: 14px;
  background: #fffaf2;
  color: inherit;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;
}

.other-reservation-card:hover,
.other-reservation-card:focus-within {
  border-color: rgba(190, 139, 66, 0.95);
  box-shadow: 0 8px 22px rgba(91, 63, 58, 0.08);
}

.other-reservation-card__thumbnail {
  width: 80px;
  height: 100%;
  min-height: 94px;
  grid-row: 1 / 3;
  grid-column: 1;
  align-self: stretch;
  overflow: hidden;
  border: 1px solid rgba(190, 160, 104, 0.42);
  border-radius: 10px;
  background-color: #ddd2cc;
  background-repeat: no-repeat;
  background-size: cover;
}

.other-reservation-card--cancelled .other-reservation-card__thumbnail {
  filter: grayscale(1);
  opacity: 0.56;
}

.other-reservation-card__summary {
  display: grid;
  min-width: 0;
  grid-row: 1;
  grid-column: 2;
  grid-template-columns: minmax(0, 1fr) auto 20px;
  align-items: center;
  gap: 10px;
  padding: 2px 0 6px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.other-reservation-card__summary:focus-visible {
  border-radius: 7px;
  outline: 2px solid rgba(215, 67, 105, 0.45);
  outline-offset: 2px;
}

.other-reservation-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
}

.other-reservation-card__title {
  overflow: hidden;
  color: #5f4844;
  font-size: 16px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.other-reservation-card__date {
  color: #8e7d78;
  font-family: "Times New Roman", serif;
  font-size: 14px;
}

.other-reservation-card__status {
  color: #927c76;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.other-reservation-card--cancelled .other-reservation-card__title,
.other-reservation-card--cancelled .other-reservation-card__date,
.other-reservation-card--cancelled .other-reservation-card__status {
  color: #a39a97;
}

.other-reservation-card__chevron {
  color: #b8a69f !important;
}

.other-reservation-card__actions {
  display: grid;
  min-width: 0;
  grid-row: 2;
  grid-column: 2;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-self: end;
  gap: 8px;
}

.other-reservation-card__actions .reservation-card-action.v-btn {
  height: 32px !important;
}

.reservation-detail {
  position: relative;
  overflow: hidden;
  overflow: clip; /* 둥근 모서리만 자르고 스크롤 칸은 만들지 않는다. 그래야 아래 단추 줄이 창 스크롤에 붙는다 */
  border-radius: 18px;
  background: #fffdf9;
  color: #5e4440;
}

.reservation-detail__header {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) 42px;
  align-items: center;
  gap: 18px;
  padding: 24px;
  border-bottom: 1px solid rgba(190, 160, 104, 0.32);
  background: #fff8ec;
}

.reservation-detail__thumbnail {
  width: 96px;
  height: 96px;
  border: 1px solid rgba(190, 160, 104, 0.48);
  border-radius: 13px;
  background-color: #ddd2cc;
  background-repeat: no-repeat;
  background-size: cover;
}

.reservation-detail__thumbnail--cancelled {
  filter: grayscale(1);
  opacity: 0.62;
}

.reservation-detail__title {
  margin: 9px 0 2px;
  color: #684945;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.1;
}


.reservation-detail__header p {
  margin: 0;
  color: #967f79;
  font-size: 13px;
}

.reservation-detail__close {
  align-self: start;
  color: #8d7a75 !important;
}

.reservation-detail__facts {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 18px 24px 4px;
}

.reservation-detail__facts > div {
  display: grid;
  grid-template-columns: minmax(108px, 0.38fr) minmax(0, 1fr);
  gap: 15px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(190, 160, 104, 0.17);
}

.reservation-detail__facts dt,
.reservation-detail__facts dd {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}

.reservation-detail__facts dt {
  color: #9a837d;
}

.reservation-detail__facts dd {
  color: #4f4542;
  font-weight: 600;
}

/* 방문시간 줄: 값 옆에 '시간 변경' 단추. 좁으면 단추가 아랫줄로 내려간다.
   값의 첫 줄을 단추 높이(32px)로 잡고 이름을 그 가운데에 맞춰, 단추가 내려가도 이름과 날짜가 같은 높이에 있다. */
.reservation-detail__time-row dt {
  padding-top: 6px;
}

.reservation-detail__time-row .reservation-detail__time > span {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
}

.reservation-detail__time {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
}

.reservation-detail__time-change {
  position: relative;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid #c64064;
  border-radius: 7px;
  background: #fff;
  color: #c64064;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  transition: background-color 160ms ease-out, transform 160ms ease-out;
}

/* 보이는 크기는 작게 두고, 누르는 자리는 44px로 넓힌다. */
.reservation-detail__time-change::after {
  position: absolute;
  inset: -6px -4px;
  content: "";
}

.reservation-detail__time-change:active {
  transform: scale(0.97);
}

.reservation-detail__time-change:focus-visible {
  outline: 2px solid #684945;
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .reservation-detail__time-change:hover {
    background: #fffaf2;
  }
}

.reservation-detail__guide {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 16px 24px 0;
  padding: 13px 14px;
  border-radius: 9px;
  background: #f8f0e4;
  /* 바탕 #f8f0e4 위 글자 대비 5.97:1(전에는 #8a746f 3.86:1로 읽기 기준 4.5:1에 못 미쳤다) */
  color: #705552;
  font-size: 13px;
  line-height: 1.55;
  word-break: keep-all;
  overflow-wrap: break-word;
}

/* 원본·완성본 두 줄: 받는 곳 안내와 한 칸 띄우고, 둘째 줄은 번호 뒤에 맞춰 들여 쓴다 */
.reservation-detail__photo-guide {
  margin: 16px 24px 0;
}

/* 글이 길어 창이 스크롤되면(영·일 휴대폰, 작은 휴대폰) 단추가 화면 밖으로 사라지지 않게 창 아래에 붙인다(10/17 검수 높음).
   위 여백만큼 옅게 사라지는 바탕이라, 창이 다 보일 때는 모양이 그대로다 */
.reservation-detail__actions {
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 22px 24px 26px;
  background: linear-gradient(to bottom, rgba(255, 253, 249, 0), #fffdf9 18px);
}

/* 단추 줄 위 칸에서 Tab·초점으로 내려갈 때 붙은 단추 줄(휴대폰 약 132px) 뒤에 가려지지 않게 그만큼 띄워 보인다(10/17 검수 2회) */
.reservation-detail__time-change,
.reservation-detail__closed,
.reservation-detail__dm {
  scroll-margin-bottom: 140px;
}

/* 단추가 하나뿐이면(방문 3일 안쪽·취소한 예약) 창 너비를 다 써, 위 안내·DM 단추와 가운데 줄이 맞는다 */
.reservation-detail__actions > :only-child {
  grid-column: 1 / -1;
}

.detail-action.v-btn {
  min-width: 0 !important;
  height: 44px !important;
  border-radius: 7px;
  font-size: 13px;
  letter-spacing: -0.02em;
}

/* 예약 상세 단추: 시간 변경 창과 같은 분홍(흰 글자 대비 4.86)과 금색 선 테두리 */
.detail-action--primary {
  background: #c64064 !important;
  color: #fff !important;
}

.detail-action--cancel {
  border-color: rgba(190, 160, 104, 0.48) !important;
  color: #705552 !important;
}

@media (max-width: 700px) {
  .quiet-schedule__body {
    padding: 0 16px;
  }

  .upcoming-reservation {
    grid-template-columns: 112px minmax(0, 1fr);
  }
}

@media (max-width: 520px) {
  .upcoming-reservation {
    grid-template-columns: 104px minmax(0, 1fr);
    gap: 10px;
    min-height: 150px;
    padding: 8px;
  }

  .upcoming-reservation__photo {
    width: 100%;
    min-height: 130px;
    aspect-ratio: 4 / 5;
  }

  .upcoming-reservation__content {
    grid-template-rows: minmax(72px, 1fr) 40px;
    gap: 6px;
    padding: 1px 1px 1px 0;
  }

  .upcoming-reservation__summary {
    grid-template-columns: minmax(0, 1fr) 18px;
    gap: 5px;
    padding-bottom: 5px;
  }

  .upcoming-reservation__title {
    display: -webkit-box;
    font-size: 15px;
    line-height: 1.25;
    white-space: normal;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .upcoming-reservation__date {
    font-size: 13px;
  }

  .upcoming-reservation__status {
    display: none;
  }

  .upcoming-reservation__actions {
    gap: 5px;
  }

  .upcoming-reservation__actions .reservation-card-action.v-btn {
    height: 40px !important;
    padding: 0 5px !important;
    font-size: 11px;
  }

  .other-reservations {
    margin-top: 24px;
  }

  .other-reservation-card {
    min-height: 124px;
    grid-template-columns: 76px minmax(0, 1fr);
    column-gap: 10px;
    padding: 8px 9px 8px 8px;
  }

  .other-reservation-card__thumbnail {
    width: 76px;
    min-height: 106px;
  }

  .other-reservation-card__summary {
    grid-template-columns: minmax(0, 1fr) 18px;
    gap: 5px;
  }

  .other-reservation-card__status {
    display: none;
  }

  .other-reservation-card__actions {
    gap: 5px;
  }

  .other-reservation-card__actions .reservation-card-action.v-btn {
    height: 40px !important;
    padding: 0 6px !important;
    font-size: 10px;
  }

  .other-reservation-card__actions .reservation-card-action.v-btn .v-icon {
    margin-right: 3px;
    font-size: 15px !important;
  }

  .quiet-schedule .reservation-card-action.v-btn {
    padding: 0 5px !important;
    font-size: 10.5px;
    letter-spacing: -0.03em;
  }

  .quiet-schedule .reservation-card-action.v-btn .v-icon {
    display: none;
  }

  .quiet-schedule .reservation-card-action__label {
    display: block;
    white-space: normal;
    line-height: 1.1;
    text-align: center;
  }

  .quiet-schedule:lang(ko) .reservation-card-action__label {
    word-break: keep-all;
  }

  .quiet-schedule .reservation-card-action--notice .reservation-card-action__label {
    max-width: 70px;
  }

  .quiet-schedule .reservation-card-action--original .reservation-card-action__label {
    max-width: 52px;
  }

  .reservation-detail__header {
    grid-template-columns: 74px minmax(0, 1fr) 36px;
    gap: 13px;
    padding: 18px;
  }

  .reservation-detail__thumbnail {
    width: 74px;
    height: 74px;
  }

  /* 휴대폰 제목 칸은 194px(390 폭). 영·일 상품 이름이 28px이면 두 줄이 되어 창이 길어지고 단추가 화면 밖으로 밀린다(10/17 검수).
     한 줄에 드는 크기로(Premium Shot 26px 183px, プレミアムショット 20px 179px). 일본어는 낱말 중간에서 갈리지 않게 */
  :lang(en) .reservation-detail__title {
    font-size: 26px;
  }

  :lang(ja) .reservation-detail__title {
    font-size: 20px;
    font-weight: 600; /* 일본어는 표제 글꼴이 없어 400이면 아래 굵은 사실 글보다 약해 보인다 */
    line-break: strict;
    word-break: auto-phrase;
  }

  .reservation-detail__facts {
    padding: 14px 18px 2px;
  }

  .reservation-detail__facts > div {
    grid-template-columns: 96px minmax(0, 1fr);
  }

  .reservation-detail__guide,
  .reservation-detail__photo-guide {
    margin: 14px 18px 0;
  }

  .reservation-detail__actions {
    grid-template-columns: 1fr;
    padding: 18px;
  }

  .reservation-detail__closed {
    margin: 14px 18px 0;
  }
}

.gallery-pass {
  color: #5e4440;
  background: #fffdf9;
}

.gallery-pass__body {
  background: transparent !important;
}

.schedule-heading {
  position: relative;
  padding: 12px 16px 14px;
  text-align: center;
}

.schedule-heading::after {
  display: block;
  width: 92px;
  height: 1px;
  margin: 16px auto 0;
  background: #d7b473;
  content: "";
}

.schedule-heading__crown {
  width: 42px;
  height: 30px;
  object-fit: contain;
  filter: sepia(0.35) saturate(0.8);
}

.schedule-heading__title {
  margin: 4px 0 0;
  color: #6a4b47;
  font-size: clamp(28px, 6vw, 36px);
  font-weight: 400;
  line-height: 1.25;
}

.reservation-gallery {
  display: grid;
  gap: 16px;
  padding: 0 18px;
}

.reservation-card {
  display: grid;
  grid-template-columns: minmax(150px, 36%) minmax(0, 1fr);
  min-height: 260px;
  overflow: hidden;
  border: 1px solid #d8b87b;
  border-radius: 18px;
  background: #fffaf0;
  box-shadow: 0 8px 20px rgba(91, 63, 58, 0.1);
}

.reservation-card--featured {
  grid-template-columns: minmax(240px, 48%) minmax(0, 1fr);
  min-height: 350px;
}

.reservation-card__photo {
  min-height: 100%;
  border-right: 1px solid rgba(216, 184, 123, 0.75);
  background-color: #ddd2cc;
  background-repeat: no-repeat;
  background-size: cover;
}

.reservation-card__photo--cancelled {
  filter: grayscale(1);
  opacity: 0.68;
}

.reservation-card__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 22px 20px 18px;
}

.reservation-card__header {
  min-width: 0;
}

.reservation-card__heading-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.reservation-card__title {
  margin: 0;
  color: #694944;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.reservation-card__date {
  margin: 6px 0 0;
  color: #392f2d;
  font-family: "Times New Roman", serif;
  font-size: 18px;
  letter-spacing: 0.01em;
}

.reservation-card__shooting-time {
  margin: 3px 0 0;
  color: #dd476b;
  font-size: 14px;
  font-weight: 700;
}

.reservation-card__divider {
  width: 48px;
  height: 1px;
  margin: 14px 0 12px;
  background: #d8b87b;
}

.reservation-card__details {
  display: grid;
  gap: 5px;
  margin: 0;
  color: #4e4643;
  font-size: 14px;
  line-height: 1.35;
}

.reservation-card__details > div {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.reservation-card__details dt,
.reservation-card__details dd {
  margin: 0;
}

.reservation-card__details dt {
  color: #88746e;
}

.reservation-card__details dd {
  font-weight: 500;
}

.reservation-card__payment {
  align-items: baseline;
  margin-top: 5px;
}

.reservation-card__payment dd:last-child {
  margin-left: 3px;
  color: #4c4644;
  font-size: 16px;
}

.reservation-card__options {
  margin-top: 8px;
  color: #806e68;
  font-size: 12px;
  line-height: 1.4;
}

.reservation-card__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
  margin-top: auto;
  padding-top: 16px;
}

.reservation-card:not(.reservation-card--featured) .reservation-card__content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 116px;
  grid-template-rows: auto auto 1fr auto;
  column-gap: 12px;
  padding: 18px 16px;
}

.reservation-card:not(.reservation-card--featured) .reservation-card__header {
  grid-column: 1 / -1;
}

.reservation-card:not(.reservation-card--featured) .reservation-card__divider,
.reservation-card:not(.reservation-card--featured) .reservation-card__details,
.reservation-card:not(.reservation-card--featured) .reservation-card__options {
  grid-column: 1;
}

.reservation-card:not(.reservation-card--featured) .reservation-card__actions {
  grid-row: 2 / 5;
  grid-column: 2;
  grid-template-columns: 1fr;
  align-content: end;
  gap: 5px;
  margin: 0;
  padding: 0;
}

.reservation-card:not(.reservation-card--featured) .gallery-action.v-btn {
  min-height: 40px;
  padding: 5px 7px !important;
  font-size: 11px;
}

.gallery-action.v-btn {
  min-width: 0 !important;
  height: auto !important;
  min-height: 42px;
  padding: 7px 10px !important;
  border-radius: 7px;
  font-size: 13px;
  letter-spacing: -0.02em;
}

.gallery-action.v-btn .v-icon {
  margin-right: 4px;
}

.gallery-action--primary {
  background: #dc3f69 !important;
  color: #fff !important;
}

.gallery-action--secondary {
  border: 1px solid #dc3f69 !important;
  background: #fffdf9 !important;
  color: #dc3f69 !important;
}

.gallery-action--text {
  color: #7d6970 !important;
  line-height: 1.05;
}

.gallery-action--cancel {
  border-color: #aab0bd !important;
  color: #596277 !important;
}

.reservation-guide {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 24px 18px 0;
  padding: 16px;
  color: #807474;
  text-align: center;
}

.reservation-guide p {
  margin: 2px 0;
  font-size: 12px;
  line-height: 1.45;
}

.reservation-guide__title {
  color: #6f5753;
  font-weight: 700;
}

.empty-reservations {
  background: #fffaf0 !important;
}

.reservation-card--cancelled {
  border-color: #cfc2be;
  background: #f4efec;
}

.cancel-status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px;
  border: 1px solid #d2838e;
  border-radius: 999px;
  color: #bd4c62;
  font-family: sans-serif;
  font-size: 12px;
  font-weight: 600;
}

.completed-payment-text {
  color: #dc3f69;
  font-weight: 700 !important;
}

.cancelled-payment-text {
  color: #bd4c62;
  font-weight: 700 !important;
}

.icon {
  width: 60px;
  height: 60px;
}
.dialog_btn {
  border: 2px solid #dadada !important;
  font-size: 16px;
}

@media (max-width: 520px) {
  .schedule-heading {
    padding: 8px 16px 10px;
  }

  .schedule-heading::after {
    width: 58px;
    margin-top: 8px;
  }

  .schedule-heading__crown {
    width: 30px;
    height: 22px;
  }

  .schedule-heading__title {
    margin-top: 2px;
    font-size: 25px;
  }

  .reservation-card,
  .reservation-card--featured {
    grid-template-columns: minmax(118px, 36%) minmax(0, 1fr);
    min-height: 300px;
  }

  .reservation-card__content {
    display: flex !important;
    padding: 18px 14px 14px;
  }

  .reservation-card__title {
    font-size: 24px;
  }

  .reservation-card__date {
    font-size: 15px;
  }

  .reservation-card__actions {
    grid-template-columns: 1fr;
    margin-top: auto !important;
    padding-top: 16px !important;
  }
}

@media (max-width: 380px) {
  .reservation-card,
  .reservation-card--featured {
    grid-template-columns: 1fr;
  }

  .reservation-card__photo {
    min-height: 190px;
    border-right: 0;
    border-bottom: 1px solid rgba(216, 184, 123, 0.75);
  }
}
</style>
